import useI18n from '@/shared/hooks/useI18n'
import { useMemo, useState } from 'react'
import AllocatedPrecursorTable from './AllocatedPrecursorTable'
import MissingResourcesAlert from '@/components/MissingResourcesAlert'
import { usePrecursorMaterialsWithPrecursorEmissionReports } from '@/hooks/api/v2/precursorMaterials'
import { LoadingScreen } from '@/shared/components'
import { useSession } from '@/shared/hooks/useSession'
import ReportingPeriodsSelector, {
  getInitialReportingPeriod
} from '../../../components/ReportingPeriodsSelector'
import { PageSection } from '@/common'

export default function PrecursorMaterials({
  installationId,
  reportingPeriods,
  reportingPeriodBasis
}: {
  installationId: string
  reportingPeriods: { startDate: string; endDate: string }[]
  reportingPeriodBasis: string
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id

  const {
    precursorMaterialsWithEmissionReports: precursorMaterials,
    isLoading: isLoadingPrecursorMaterials
  } = usePrecursorMaterialsWithPrecursorEmissionReports(organizationId, installationId)

  const [selectedReportingPeriod, setSelectedReportingPeriod] = useState<{
    value: string | null
    startDate?: string
    endDate?: string
  }>(() => getInitialReportingPeriod(reportingPeriods))

  const allocatedMaterials = useMemo(() => {
    if (!precursorMaterials) {return []}

    const isMaterialInReportingPeriod = (material: any): boolean => {
      if (!selectedReportingPeriod.startDate || !selectedReportingPeriod.endDate) {
        return true
      }

      const startDate = new Date(material.emissionReport.startDate)
      const endDate = new Date(material.emissionReport.endDate)

      // Check if the emission report dates fall within the selected reporting period
      return (
        startDate >= new Date(selectedReportingPeriod.startDate) &&
        endDate <= new Date(selectedReportingPeriod.endDate)
      )
    }

    return precursorMaterials.filter(material => {
      const isAllocated = material.stockChangeType === 'ALLOCATED'
      const isInReportingPeriod = isMaterialInReportingPeriod(material)

      return isAllocated && isInReportingPeriod
    })
  }, [precursorMaterials, selectedReportingPeriod])

  const hasAllocatedMaterials = allocatedMaterials && allocatedMaterials.length > 0

  if (isLoadingPrecursorMaterials) {
    return <LoadingScreen />
  }

  return (
    <>
      <PageSection
        title={t('materials:balancePage.sections.allocated.title')}
        actions={
          <ReportingPeriodsSelector
            reportingPeriods={reportingPeriods}
            reportingPeriodBasis={reportingPeriodBasis}
            selectedReportingPeriod={selectedReportingPeriod}
            onChange={(value, startDate, endDate) => {
              setSelectedReportingPeriod({ value, startDate, endDate })
            }}
          />
        }
      >
        {hasAllocatedMaterials && <AllocatedPrecursorTable materials={allocatedMaterials} />}
        {!hasAllocatedMaterials && (
          <MissingResourcesAlert
            title={t('materials:allocatedStockTable.noData.title')}
            description={t('materials:allocatedStockTable.noData.description')}
          />
        )}
      </PageSection>
    </>
  )
}
